import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Scroll, Plus, FilmSlate, Trash, Copy, PencilSimple, Check, X, Sparkle } from '@phosphor-icons/react'
import api from '../services/api'
import { toast } from 'sonner'
import ScriptEditor from '../components/ScriptEditor'
import { GiftScriptBanner } from '../components/GiftScriptBanner'
import DraftImportModal from '../components/DraftImportModal'
import { useOnboardingGift } from '../hooks/useOnboardingGift'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog'

const STATUS_COLORS = {
  idea: '#FFD60A',
  plan: '#5E5CE6',
  record: '#D42B1E',
  edit: '#FF9F0A',
  analyze: '#007AFF',
  publish: '#32ADE6',
}

// Inline rename input for version names
function InlineRename({ value, onSave, onCancel }) {
  const [name, setName] = useState(value)
  const inputRef = useRef(null)
  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])
  return (
    <div className="flex items-center gap-1 min-w-0" onClick={e => e.stopPropagation()}>
      <input
        ref={inputRef}
        value={name}
        onChange={e => setName(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') onSave(name)
          if (e.key === 'Escape') onCancel()
        }}
        className="bg-ds-elevated border border-[rgba(212,43,30,0.35)] px-1.5 py-0.5 text-ds-text text-[11px] focus:outline-none min-w-0 w-28"
        style={{ borderRadius: 2 }}
      />
      <button onClick={() => onSave(name)} className="p-0.5 text-green-400 hover:text-green-300">
        <Check size={11} />
      </button>
      <button onClick={onCancel} className="p-0.5 text-[#6E6E73] hover:text-white">
        <X size={11} />
      </button>
    </div>
  )
}

function DuplicateScriptDialog({ open, onClose, script, onCreate }) {
  const [versionName, setVersionName] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (open && script) setVersionName(`${script.version_name} (copia)`)
  }, [open, script])

  const handleDuplicate = async () => {
    setLoading(true)
    try {
      const res = await api.post('/scripts', {
        project_id: script.project_id,
        version_name: versionName,
      })
      await api.put(`/scripts/${res.data.id}`, {
        sections: script.sections,
        context_text: script.context_text,
      })
      const updated = await api.get(`/scripts/${res.data.id}`)
      onCreate(updated.data)
      onClose()
      toast.success('Versión duplicada')
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al duplicar')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="bg-ds-surface border border-ds-border text-ds-text max-w-sm"
        style={{ borderRadius: 2 }}
      >
        <DialogHeader>
          <DialogTitle className="type-h3 text-ds-text text-base">Duplicar versión</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <input
            value={versionName}
            onChange={e => setVersionName(e.target.value)}
            className="ds-input"
            data-testid="duplicate-version-name-input"
          />
          <div className="flex gap-3">
            <button onClick={onClose} className="btn-ghost flex-1 py-2">
              Cancelar
            </button>
            <button
              onClick={handleDuplicate}
              disabled={loading}
              className="btn-accent flex-1 py-2 disabled:opacity-50"
              data-testid="confirm-duplicate-btn"
            >
              {loading ? '...' : 'Duplicar'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/* ── Version switcher (horizontal pills above the editor) ────────────────── */
function VersionSwitcher({
  versions,
  activeId,
  canCreate,
  creating,
  renamingId,
  onSelect,
  onCreate,
  onDuplicate,
  onDelete,
  onRename,
  onStartRename,
  onCancelRename,
}) {
  if (!versions?.length) return null
  return (
    <div className="flex items-center gap-1.5 px-3 md:px-4 py-2 border-b border-ds-border bg-[#0F0F0F] overflow-x-auto shrink-0">
      <span className="type-label text-[#6E6E73] shrink-0 mr-1 text-[9px] tracking-wider">
        VERSIONES
      </span>
      {versions.map(v => {
        const active = v.id === activeId
        const isRenaming = renamingId === v.id
        return (
          <div
            key={v.id}
            className={`group shrink-0 flex items-center gap-1 px-2 py-1 border transition-colors ${
              active
                ? 'bg-[rgba(212,43,30,0.12)] border-ds-red'
                : 'border-ds-border hover:border-[#3A3A3A] hover:bg-ds-elevated'
            }`}
            style={{ borderRadius: 2 }}
            data-testid={`version-pill-${v.id}`}
          >
            {isRenaming ? (
              <InlineRename
                value={v.version_name}
                onSave={name => onRename(v.id, name)}
                onCancel={onCancelRename}
              />
            ) : (
              <>
                <button
                  onClick={() => onSelect(v.id)}
                  className={`text-[11px] truncate max-w-[180px] ${
                    active ? 'text-white font-medium' : 'text-[#A1A1A6] hover:text-white'
                  }`}
                  title={v.version_name}
                >
                  {v.version_name}
                </button>
                {active && (
                  <div className="flex items-center gap-0.5 ml-1 pl-1 border-l border-[rgba(212,43,30,0.3)]">
                    <button
                      onClick={() => onStartRename(v.id)}
                      className="p-0.5 text-[#6E6E73] hover:text-white transition-colors"
                      title="Renombrar"
                      data-testid={`rename-script-${v.id}`}
                    >
                      <PencilSimple size={9} />
                    </button>
                    <button
                      onClick={() => onDuplicate(v)}
                      disabled={!canCreate}
                      className="p-0.5 text-[#6E6E73] hover:text-[#007AFF] transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-[#6E6E73]"
                      title={canCreate ? 'Duplicar' : 'Solo disponible en fase Idea o Plan'}
                      data-testid={`duplicate-script-${v.id}`}
                    >
                      <Copy size={9} />
                    </button>
                    <button
                      onClick={() => onDelete(v.id)}
                      className="p-0.5 text-[#6E6E73] hover:text-ds-red transition-colors"
                      title="Eliminar"
                      data-testid={`delete-script-item-${v.id}`}
                    >
                      <Trash size={9} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )
      })}
      <button
        onClick={onCreate}
        disabled={!canCreate || creating}
        className="shrink-0 flex items-center gap-1 px-2 py-1 text-[11px] text-[#6E6E73] hover:text-white border border-dashed border-ds-border hover:border-ds-red transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-[#6E6E73] disabled:hover:border-ds-border"
        style={{ borderRadius: 2 }}
        title={canCreate ? 'Nueva versión' : 'Solo disponible en fase Idea o Plan'}
        data-testid="add-version-switcher"
      >
        {creating ? (
          <div className="w-2.5 h-2.5 border border-[#D42B1E] border-t-transparent rounded-full animate-spin" />
        ) : (
          <Plus size={10} weight="bold" />
        )}
        Nueva
      </button>
    </div>
  )
}

export default function ScriptsPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const [scripts, setScripts] = useState([])
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedScriptId, setSelectedScriptId] = useState(null)
  const [duplicateTarget, setDuplicateTarget] = useState(null)
  const [showMobileEditor, setShowMobileEditor] = useState(false)
  const [creatingFor, setCreatingFor] = useState(null)
  const [renamingScriptId, setRenamingScriptId] = useState(null)
  const [editorDirty, setEditorDirty] = useState(false)
  const [showDraftImport, setShowDraftImport] = useState(false)
  const { giftScriptId, clearGift } = useOnboardingGift()

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [sc, pr] = await Promise.all([api.get('/scripts'), api.get('/projects')])
      setScripts(sc.data)
      setProjects(pr.data)
    } catch {
      toast.error('Error al cargar guiones')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Auto-select gift script when landing from onboarding (F1.b)
  useEffect(() => {
    if (!giftScriptId || loading) return
    const exists = scripts.some(s => s.id === giftScriptId)
    if (exists) {
      setSelectedScriptId(giftScriptId)
      setShowMobileEditor(true)
    }
  }, [giftScriptId, loading, scripts])

  // Auto-create or auto-select when navigating from Dashboard
  useEffect(() => {
    const pid = location.state?.projectId
    const resurrectId = location.state?.resurrectedScriptId
    if (!pid || loading) return

    // Clear state immediately so back-navigation doesn't re-trigger
    navigate('/scripts', { replace: true, state: {} })

    if (resurrectId) {
      // Coming from a draft import — re-fetch and select the resurrected script
      fetchData().then(() => {
        setSelectedScriptId(resurrectId)
        setShowMobileEditor(true)
      })
      return
    }

    const projectScripts = scripts
      .filter(s => s.project_id === pid)
      .sort((a, b) => (a.created_at > b.created_at ? -1 : 1))

    if (projectScripts.length > 0) {
      setSelectedScriptId(projectScripts[0].id)
      setShowMobileEditor(true)
    } else {
      autoCreateScript(pid)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, loading])

  const autoCreateScript = async (pid, versionName) => {
    const project = projects.find(p => p.id === pid)
    if (project && !['idea', 'plan'].includes(project.status)) {
      toast.error('Solo puedes crear guiones para proyectos en fase Idea o Plan')
      return
    }
    const count = scripts.filter(s => s.project_id === pid).length
    const name = versionName || (count === 0 ? 'v1 - Borrador' : `v${count + 1} - Borrador`)
    setCreatingFor(pid)
    try {
      const res = await api.post('/scripts', { project_id: pid, version_name: name })
      setScripts(prev => [res.data, ...prev])
      setSelectedScriptId(res.data.id)
      setShowMobileEditor(true)
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear guión')
    } finally {
      setCreatingFor(null)
    }
  }

  const renameScript = async (scriptId, newName) => {
    if (!newName.trim()) {
      setRenamingScriptId(null)
      return
    }
    try {
      await api.put(`/scripts/${scriptId}`, { version_name: newName.trim() })
      setScripts(prev =>
        prev.map(s => (s.id === scriptId ? { ...s, version_name: newName.trim() } : s))
      )
    } catch {
      toast.error('Error al renombrar')
    } finally {
      setRenamingScriptId(null)
    }
  }

  // Click on a project in the sidebar: open its most recent version,
  // or auto-create a first version if none exists and status allows it.
  const selectProject = pid => {
    const projectScripts = scripts
      .filter(s => s.project_id === pid)
      .sort((a, b) => (a.created_at > b.created_at ? -1 : 1))
    if (projectScripts.length > 0) {
      selectScript(projectScripts[0].id)
      return
    }
    const project = projects.find(p => p.id === pid)
    if (project && ['idea', 'plan'].includes(project.status)) {
      autoCreateScript(pid)
    } else {
      toast.error('Solo puedes crear guiones para proyectos en fase Idea o Plan')
    }
  }

  const selectScript = id => {
    if (id === selectedScriptId) return
    if (
      editorDirty &&
      !window.confirm('Tienes cambios sin guardar. ¿Cambiar de guión y perderlos?')
    )
      return
    setEditorDirty(false)
    setSelectedScriptId(id)
    setShowMobileEditor(true)
  }

  // Handle post-deletion state (shared between switcher delete button and editor onDeleted)
  const handleScriptDeleted = id => {
    const deleted = scripts.find(s => s.id === id)
    setScripts(prev => prev.filter(s => s.id !== id))
    if (selectedScriptId !== id) return
    // Was the active one — fall back to a sibling of the same project
    const siblings = deleted
      ? scripts
          .filter(s => s.project_id === deleted.project_id && s.id !== id)
          .sort((a, b) => (a.created_at > b.created_at ? -1 : 1))
      : []
    if (siblings.length > 0) {
      setSelectedScriptId(siblings[0].id)
    } else {
      setSelectedScriptId(null)
      setShowMobileEditor(false)
    }
    setEditorDirty(false)
  }

  const deleteScriptFromSwitcher = async scriptId => {
    if (!window.confirm('¿Eliminar esta versión?')) return
    try {
      await api.delete(`/scripts/${scriptId}`)
      handleScriptDeleted(scriptId)
      toast.success('Versión eliminada')
    } catch {
      toast.error('Error al eliminar')
    }
  }

  const onScriptCreated = script => {
    setScripts(prev => [script, ...prev])
    setSelectedScriptId(script.id)
    setShowMobileEditor(true)
  }

  const onScriptSaved = updated =>
    setScripts(prev => prev.map(s => (s.id === updated.id ? updated : s)))

  // All projects, sorted: those with scripts first, then by title
  const sortedProjects = [...projects].sort((a, b) => {
    const aHas = scripts.some(s => s.project_id === a.id) ? 0 : 1
    const bHas = scripts.some(s => s.project_id === b.id) ? 0 : 1
    if (aHas !== bHas) return aHas - bHas
    return a.title.localeCompare(b.title)
  })

  const totalVersions = scripts.length

  // Derive the active project and its versions from selectedScriptId
  const activeScript = scripts.find(s => s.id === selectedScriptId) || null
  const activeProjectId = activeScript?.project_id || null
  const activeProject = activeProjectId
    ? projects.find(p => p.id === activeProjectId) || null
    : null
  const activeProjectVersions = activeProjectId
    ? scripts
        .filter(s => s.project_id === activeProjectId)
        .sort((a, b) => (a.created_at > b.created_at ? 1 : -1)) // oldest → newest
    : []
  const activeProjectCanCreate = activeProject
    ? ['idea', 'plan'].includes(activeProject.status)
    : false
  const activeProjectCreating = creatingFor === activeProjectId

  return (
    <div className="flex h-full">
      {/* ── Left Panel ─────────────────────────────── */}
      <div
        className={`${showMobileEditor ? 'hidden lg:flex' : 'flex'} lg:flex w-full lg:w-[300px] shrink-0 flex-col bg-ds-bg border-r border-ds-border h-full`}
      >
        {/* Panel Header */}
        <div className="flex items-center justify-between px-3 md:px-4 py-3 md:py-4 border-b border-ds-border shrink-0">
          <div className="flex items-center gap-2">
            <Scroll size={15} className="text-ds-red" weight="duotone" />
            <h2 className="type-label text-ds-text">Guiones</h2>
            {totalVersions > 0 && (
              <span className="badge-muted">
                {totalVersions} {totalVersions === 1 ? 'versión' : 'versiones'}
              </span>
            )}
          </div>
        </div>

        {/* Projects List — one row per project, no expansion */}
        <div className="flex-1 overflow-y-auto py-2">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-5 h-5 border-2 border-ds-red border-t-transparent rounded-full animate-spin" />
            </div>
          ) : projects.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <FilmSlate size={36} className="text-[#3A3A3A] mb-3" weight="duotone" />
              <p className="text-white text-sm font-medium mb-1">Sin vídeos</p>
              <p className="text-[#6E6E73] text-xs">Crea vídeos en el Dashboard para empezar</p>
            </div>
          ) : (
            sortedProjects.map(project => {
              const pCount = scripts.filter(s => s.project_id === project.id).length
              const hasScripts = pCount > 0
              const canCreateScript = ['idea', 'plan'].includes(project.status)
              const isActive = activeProjectId === project.id
              const isCreating = creatingFor === project.id

              return (
                <button
                  key={project.id}
                  onClick={() => selectProject(project.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 text-left transition-colors ${
                    isActive
                      ? 'bg-[rgba(212,43,30,0.08)] border-r-2 border-ds-red'
                      : 'hover:bg-ds-elevated'
                  }`}
                  data-testid={`project-row-${project.id}`}
                >
                  <FilmSlate
                    size={13}
                    className={isActive ? 'text-ds-red shrink-0' : 'text-[#6E6E73] shrink-0'}
                    weight="duotone"
                  />
                  <span
                    className={`flex-1 text-xs truncate ${
                      isActive ? 'text-white font-medium' : 'text-[#A1A1A6]'
                    }`}
                  >
                    {project.title}
                  </span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: STATUS_COLORS[project.status] || '#6E6E73' }}
                      title={project.status}
                    />
                    {hasScripts ? (
                      <span
                        className={`text-[10px] ${isActive ? 'text-[#A1A1A6]' : 'text-[#6E6E73]'}`}
                        title={`${pCount} ${pCount === 1 ? 'versión' : 'versiones'}`}
                      >
                        {pCount}
                      </span>
                    ) : canCreateScript ? (
                      isCreating ? (
                        <div className="w-3 h-3 border border-[#D42B1E] border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Plus size={11} className="text-[#6E6E73]" weight="bold" />
                      )
                    ) : null}
                  </div>
                </button>
              )
            })
          )}
        </div>
      </div>

      {/* ── Right Panel — Editor ─────────────────── */}
      <div
        className={`${!showMobileEditor ? 'hidden lg:flex' : 'flex'} flex-1 flex-col min-h-0 min-w-0 overflow-hidden`}
      >
        {selectedScriptId ? (
          <>
            {/* Mobile back */}
            <div className="lg:hidden flex items-center gap-2 px-4 py-3 border-b border-ds-border bg-[#141414]">
              <button
                onClick={() => setShowMobileEditor(false)}
                className="text-[#A1A1A6] text-sm flex items-center gap-1"
              >
                ← Guiones
              </button>
              {activeProject && (
                <span className="text-[#6E6E73] text-xs truncate">· {activeProject.title}</span>
              )}
            </div>
            {/* Version switcher */}
            <VersionSwitcher
              versions={activeProjectVersions}
              activeId={selectedScriptId}
              canCreate={activeProjectCanCreate}
              creating={activeProjectCreating}
              renamingId={renamingScriptId}
              onSelect={selectScript}
              onCreate={() => activeProjectId && autoCreateScript(activeProjectId)}
              onDuplicate={script => {
                if (!activeProjectCanCreate) return
                setDuplicateTarget(script)
              }}
              onDelete={deleteScriptFromSwitcher}
              onRename={renameScript}
              onStartRename={id => setRenamingScriptId(id)}
              onCancelRename={() => setRenamingScriptId(null)}
            />
            {giftScriptId && giftScriptId === selectedScriptId && (
              <GiftScriptBanner onDismiss={clearGift} />
            )}
            <ScriptEditor
              key={selectedScriptId}
              scriptId={selectedScriptId}
              onSaved={onScriptSaved}
              onDirtyChange={setEditorDirty}
              onDeleted={handleScriptDeleted}
            />
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center px-8">
            <Scroll size={56} className="text-[#2A2A2A] mb-5" weight="duotone" />
            <h3
              className="text-white text-xl font-bold mb-2"
              style={{ fontFamily: 'Barlow, sans-serif' }}
            >
              Selecciona un vídeo
            </h3>
            <p className="text-[#6E6E73] text-sm max-w-xs mb-5">
              Elige un vídeo del panel izquierdo para ver o crear su guión
            </p>
            <button
              type="button"
              onClick={() => setShowDraftImport(true)}
              className="btn-ghost flex items-center gap-2 px-4 py-2"
              data-testid="draft-import-entry-empty"
            >
              <Sparkle size={14} weight="fill" className="text-ds-red" />
              ¿Tienes un borrador abandonado? Termínalo en tu voz
            </button>
          </div>
        )}
      </div>

      {/* Dialogs */}
      <DuplicateScriptDialog
        open={!!duplicateTarget}
        onClose={() => setDuplicateTarget(null)}
        script={duplicateTarget}
        onCreate={onScriptCreated}
      />
      <DraftImportModal
        open={showDraftImport}
        onClose={() => setShowDraftImport(false)}
        onImported={() => {
          // Refetch happens via location.state effect; nothing to do here.
        }}
      />
    </div>
  )
}
